/**
 * 解析 M3U8 主播放列表（Master Playlist）
 * @param {string} m3u8Content M3U8 文件内容
 * @returns {object} 解析后的流信息对象
 */
export interface ParsedM3U8Info {
  /** 流名称 */
  streamName: string;
  /** 是否包含独立片段标记（#EXT-X-INDEPENDENT-SEGMENTS） */
  hasIndependentSegments: boolean;
  /** 媒体信息列表（音频、字幕等），对应 #EXT-X-MEDIA 标签 */
  media: Array<{
    /** 媒体类型，如 AUDIO、SUBTITLES 等 */
    type: string;
    /** 媒体组 ID，用于关联音频和视频流 */
    groupId: string;
    /** 媒体播放列表 URI */
    uri: string;
    /** 媒体名称 */
    name: string;
    /** 是否自动选择该媒体 */
    autoselect: boolean;
    /** 是否为默认媒体 */
    default: boolean;
    /** 其他扩展属性 */
    [key: string]: any;
  }>;
  /** 视频流信息列表，对应 #EXT-X-STREAM-INF 标签 */
  streams: Array<{
    /** 带宽值（bps），用于自适应流选择 */
    bandwidth: number;
    /** 视频分辨率（可选） */
    resolution?: {
      /** 视频宽度（像素） */
      width: number;
      /** 视频高度（像素） */
      height: number;
    };
    /** 帧率信息（可选） */
    frameRate?: {
      /** 帧率分子 */
      numerator: number;
      /** 帧率分母 */
      denominator: number;
    };
    /** 编解码器列表，如 ["hvc1.1.2.L120"] */
    codecs: string[];
    /** 关联的音频组 ID（可选） */
    audio?: string;
    /** 视频流播放列表 URI */
    uri: string;
    /** 其他扩展属性 */
    [key: string]: any;
  }>;
}

export function parseM3U8MasterPlaylist(m3u8Content: string, streamName: string): ParsedM3U8Info {
  const lines = m3u8Content.split(/\r?\n/).filter((line) => line.trim());
  const result: ParsedM3U8Info = {
    streamName: streamName,
    hasIndependentSegments: false,
    media: [],
    streams: [],
  };

  let currentStream: any = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // 解析 #EXT-X-INDEPENDENT-SEGMENTS
    if (line === "#EXT-X-INDEPENDENT-SEGMENTS") {
      result.hasIndependentSegments = true;
      continue;
    }

    // 解析 #EXT-X-MEDIA
    if (line.startsWith("#EXT-X-MEDIA:")) {
      const mediaTag = line.substring("#EXT-X-MEDIA:".length);
      const media: any = {};

      // 解析属性
      const attributes = mediaTag.match(/(\w+)=("([^"]+)"|([^,]+))/g) || [];
      attributes.forEach((attr: string) => {
        const match = attr.match(/(\w+)=("?)([^"]+)\2/);
        if (match) {
          const key = match[1].toLowerCase();
          let value: any = match[3];

          // 处理布尔值
          if (value === "YES") value = true;
          else if (value === "NO") value = false;

          media[key] = value;
        }
      });

      result.media.push(media);
      continue;
    }

    // 解析 #EXT-X-STREAM-INF
    if (line.startsWith("#EXT-X-STREAM-INF:")) {
      const streamInfTag = line.substring("#EXT-X-STREAM-INF:".length);
      currentStream = {};

      // 解析属性
      const attributes = streamInfTag.match(/(\w+)=("([^"]+)"|([^,]+))/g) || [];
      attributes.forEach((attr: string) => {
        const match = attr.match(/(\w+)=("?)([^"]+)\2/);
        if (match) {
          const key = match[1].toLowerCase();
          let value: any = match[3];

          // 解析 BANDWIDTH
          if (key === "bandwidth") {
            value = parseInt(value, 10);
          }
          // 解析 RESOLUTION
          else if (key === "resolution") {
            const [width, height] = value.split("x").map(Number);
            value = { width, height };
          }
          // 解析 FRAME-RATE
          else if (key === "frame-rate") {
            const [numerator, denominator] = value.split("/").map(Number);
            value = { numerator, denominator: denominator || 1 };
          }
          // 解析 CODECS
          else if (key === "codecs") {
            value = value.split(",").map((c: string) => c.trim());
          }

          currentStream[key] = value;
        }
      });

      // 下一行应该是 URI
      if (i + 1 < lines.length && !lines[i + 1].trim().startsWith("#")) {
        currentStream.uri = lines[i + 1].trim();
        result.streams.push(currentStream);
        i++; // 跳过 URI 行
      }
      continue;
    }
  }

  return result;
}

/**
 * 使用示例：
 *
 * const m3u8Content = `#EXT-X-INDEPENDENT-SEGMENTS
 *
 * #EXT-X-MEDIA:TYPE=AUDIO,GROUP-ID="agroup",URI="audio.m3u8",NAME="audio",AUTOSELECT=YES,DEFAULT=YES
 *
 * #EXT-X-STREAM-INF:BANDWIDTH=2419413,RESOLUTION=1920x1080,FRAME-RATE=30/1,CODECS="hvc1.1.2.L120",AUDIO="agroup"
 *
 * video.m3u8`;
 *
 * const parsed = parseM3U8MasterPlaylist(m3u8Content);
 * console.log(parsed);
 * // 输出：
 * // {
 * //   streamName: "",
 * //   hasIndependentSegments: true,
 * //   media: [{
 * //     type: "AUDIO",
 * //     groupid: "agroup",
 * //     uri: "audio.m3u8",
 * //     name: "audio",
 * //     autoselect: true,
 * //     default: true
 * //   }],
 * //   streams: [{
 * //     bandwidth: 2419413,
 * //     resolution: { width: 1920, height: 1080 },
 * //     framerate: { numerator: 30, denominator: 1 },
 * //     codecs: ["hvc1.1.2.L120"],
 * //     audio: "agroup",
 * //     uri: "video.m3u8"
 * //   }]
 * // }
 */
